import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Mail, Save, RotateCcw, Search, Server, Send, Eye, Activity, CheckCircle2, XCircle, MinusCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function EmailConfiguration() {
  const [tab, setTab] = useState("smtp"); // smtp | templates

  return (
    <div className="space-y-6" data-testid="email-config-page">
      <div>
        <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Administration</div>
        <h1 className="mt-1 font-heading font-bold text-3xl tracking-tight">Email Configuration</h1>
        <p className="text-slate-500 text-[13px] mt-1">Control the SMTP relay, subject / body / recipients for every automated email Ops IQ sends. Use <span className="font-mono">{"{{token}}"}</span> placeholders — they get replaced with real record data at send time.</p>
      </div>

      {/* Tab strip */}
      <div className="border-b border-slate-200 flex gap-6 text-[13px]" data-testid="email-config-tabs">
        {[
          { key: "smtp", label: "SMTP / Sender", icon: Server },
          { key: "templates", label: "Trigger Templates", icon: Mail },
          { key: "log", label: "Recent Activity", icon: Activity },
          { key: "wa_log", label: "WhatsApp Activity", icon: Send },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`email-config-tab-${t.key}`} className={`inline-flex items-center gap-2 pb-3 -mb-px border-b-2 transition-colors ${tab === t.key ? "border-brand text-brand font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "smtp" ? <SmtpPanel /> : tab === "templates" ? <TemplatesPanel /> : tab === "log" ? <LogPanel /> : <WhatsAppActivityPanel />}
    </div>
  );
}

// ---------------- SMTP panel ----------------
function SmtpPanel() {
  const [cfg, setCfg] = useState(null);
  const [pwd, setPwd] = useState(""); // typed but not yet stored — only sent on save
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/email-config"); setCfg(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...cfg };
      if (pwd) body.smtp_password = pwd;
      await api.put("/admin/email-config", body);
      toast.success("Email configuration saved");
      setPwd(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const clearPassword = async () => {
    if (!confirm("Clear the stored SMTP password?")) return;
    setBusy(true);
    try { await api.put("/admin/email-config", { clear_smtp_password: true }); toast.success("Password cleared"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Clear failed"); }
    setBusy(false);
  };

  if (!cfg) return <div className="p-4 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200 rounded-md bg-white" data-testid="smtp-form">
        {/* Provider + enabled */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-heading font-bold text-lg">SMTP Relay</div>
            <div className="text-[12.5px] text-slate-500">Ops IQ posts email through a standard SMTP server. Google Workspace SMTP relay works out of the box on <span className="font-mono">smtp-relay.gmail.com:587</span>.</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[12px] uppercase tracking-widest text-slate-500">Delivery</span>
            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="smtp-enabled-switch" />
            <span className={`text-[11px] font-semibold ${cfg.enabled ? "text-emerald-700" : "text-slate-400"}`}>{cfg.enabled ? "ENABLED" : "DISABLED"}</span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="SMTP host">
            <Input value={cfg.smtp_host || ""} onChange={(e) => setCfg({ ...cfg, smtp_host: e.target.value })} placeholder="smtp-relay.gmail.com" data-testid="smtp-host" className="font-mono" />
          </Field>
          <Field label="SMTP port">
            <Input type="number" value={cfg.smtp_port || 587} onChange={(e) => setCfg({ ...cfg, smtp_port: parseInt(e.target.value || 0, 10) })} data-testid="smtp-port" className="font-mono" />
          </Field>
          <Field label="Username (leave blank for anonymous relay)">
            <Input value={cfg.smtp_user || ""} onChange={(e) => setCfg({ ...cfg, smtp_user: e.target.value })} placeholder="opsiq@bbsglobal.in" data-testid="smtp-user" className="font-mono" />
          </Field>
          <Field label={<>Password {cfg.smtp_password_set && <span className="text-emerald-700 text-[10.5px] ml-1">· stored</span>}</>}>
            <div className="flex items-center gap-2">
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={cfg.smtp_password_set ? "•••••••••• (leave blank to keep)" : "Enter password"} data-testid="smtp-password" className="font-mono" />
              {cfg.smtp_password_set && <Button type="button" variant="outline" size="sm" onClick={clearPassword} disabled={busy} data-testid="smtp-password-clear">Clear</Button>}
            </div>
          </Field>
          <Field label="From email">
            <Input value={cfg.from_email || ""} onChange={(e) => setCfg({ ...cfg, from_email: e.target.value })} placeholder="opsiq@bbsglobal.in" data-testid="smtp-from-email" className="font-mono" />
          </Field>
          <Field label="From name">
            <Input value={cfg.from_name || ""} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} placeholder="Ops IQ · BBS Global" data-testid="smtp-from-name" />
          </Field>
          <Field label="Reply-to (optional)">
            <Input value={cfg.reply_to || ""} onChange={(e) => setCfg({ ...cfg, reply_to: e.target.value })} placeholder="ops@bbsglobal.in" data-testid="smtp-reply-to" className="font-mono" />
          </Field>
          <Field label="Use STARTTLS">
            <label className="inline-flex items-center gap-2 h-10">
              <input type="checkbox" checked={!!cfg.use_tls} onChange={(e) => setCfg({ ...cfg, use_tls: e.target.checked })} className="accent-[#7C1D3A]" data-testid="smtp-tls" />
              <span className="text-[13px] text-slate-700">Upgrade to TLS after connect (recommended)</span>
            </label>
          </Field>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="smtp-save-btn"><Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save"}</Button>
        </div>

        <div className="mt-5 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[12.5px] text-emerald-900">
          <div className="font-semibold mb-1">Setup — reuse systemalert@bbsglobal.in (same as Asset IQ)</div>
          <div className="mb-2 opacity-90">
            <span className="font-mono">opsiq@bbsglobal.in</span> is an alias of <span className="font-mono">systemalert@bbsglobal.in</span>, so it uses the exact same authentication as <span className="font-mono">assetiq@bbsglobal.in</span>. Nothing new to create — just fill the four fields below:
          </div>
          <ol className="list-decimal pl-5 space-y-0.5">
            <li><b>SMTP host</b>: <span className="font-mono">smtp-relay.gmail.com</span> · <b>Port</b>: <span className="font-mono">587</span> · <b>TLS</b>: on</li>
            <li><b>Username</b>: <span className="font-mono">systemalert@bbsglobal.in</span> (the real mailbox — same one Asset IQ authenticates as)</li>
            <li><b>Password</b>: the App Password you generated for <span className="font-mono">systemalert@bbsglobal.in</span> (paste the same 16-char password Asset IQ uses)</li>
            <li><b>From email</b>: <span className="font-mono">opsiq@bbsglobal.in</span> — Gmail will honour this alias because it's registered under systemalert's identities</li>
          </ol>
          <div className="mt-2 text-[11.5px] opacity-80">
            No Google Admin changes needed — Asset IQ's SMTP relay rule already allows any @bbsglobal.in sender. After saving, head to <b>Notifications → Send Test</b> to confirm delivery.
          </div>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}

// ---------------- Templates panel ----------------
function TemplatesPanel() {
  const [triggers, setTriggers] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/email-triggers");
    setTriggers(data);
    if (!selectedKey && data.length) setSelectedKey(data[0].key);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const grouped = useMemo(() => {
    const filtered = triggers.filter((t) => {
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return t.name.toLowerCase().includes(s) || t.description.toLowerCase().includes(s) || t.key.toLowerCase().includes(s);
    });
    const byCat = {};
    filtered.forEach((t) => { (byCat[t.category] ||= []).push(t); });
    return byCat;
  }, [triggers, q]);

  const selected = triggers.find((t) => t.key === selectedKey);

  const saveTrigger = async (updates) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/email-triggers/${selected.key}`, updates);
      setTriggers((prev) => prev.map((t) => (t.key === data.key ? data : t)));
      toast.success("Trigger saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const resetTrigger = async () => {
    if (!confirm("Reset subject and body to the default template? Your recipients and enabled state will be kept.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email-triggers/${selected.key}/reset`);
      setTriggers((prev) => prev.map((t) => (t.key === data.key ? data : t)));
      toast.success("Template reset to default");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    setBusy(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
      {/* Left rail */}
      <Card className="border-slate-200 rounded-md bg-white overflow-hidden" data-testid="triggers-list">
        <div className="p-3 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search triggers…" data-testid="triggers-search" className="w-full h-9 pl-9 pr-3 rounded-md bg-slate-50 border border-slate-200 text-sm outline-none focus:bg-white focus:border-slate-300" />
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="border-b border-slate-100 last:border-b-0">
              <div className="px-3 pt-3 pb-1.5 text-[10.5px] font-mono uppercase tracking-widest text-slate-400">{cat}</div>
              {list.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setSelectedKey(t.key)}
                  data-testid={`trigger-${t.key}`}
                  className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 border-l-2 transition-colors ${selectedKey === t.key ? "bg-[#7C1D3A]/5 border-[#7C1D3A]" : "border-transparent"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-[13px] truncate">{t.name}</div>
                    <div className="text-[10px] text-slate-400 whitespace-nowrap">{((t.to_recipients || t.recipients) || []).length + ((t.cc_recipients) || []).length + ((t.to_role_recipients || t.role_recipients) || []).length + ((t.cc_role_recipients) || []).length} rec.</div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${t.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {t.enabled ? "Enabled" : "Disabled"}
                    </span>
                    <span className="text-[10.5px] font-mono text-slate-400 truncate">{t.key}</span>
                  </div>
                </button>
              ))}
            </div>
          ))}
          {Object.keys(grouped).length === 0 && <div className="px-3 py-8 text-center text-[12px] text-slate-500">No triggers match.</div>}
        </div>
      </Card>

      {/* Right editor */}
      <div className="min-w-0">
        {selected ? (
          <TriggerEditor key={selected.key} trigger={selected} onSave={saveTrigger} onReset={resetTrigger} busy={busy} />
        ) : (
          <Card className="p-8 text-center text-slate-500">Select a trigger from the left to edit its template.</Card>
        )}
      </div>
    </div>
  );
}

function TriggerEditor({ trigger, onSave, onReset, busy }) {
  const [subject, setSubject] = useState(trigger.subject || "");
  const [body, setBody] = useState(trigger.body_html || "");
  const [enabled, setEnabled] = useState(!!trigger.enabled);
  const [recipientsTo, setRecipientsTo] = useState((trigger.to_recipients || trigger.recipients || []).join(", "));
  const [recipientsCc, setRecipientsCc] = useState((trigger.cc_recipients || []).join(", "));
  const [roleRecipientsTo, setRoleRecipientsTo] = useState(new Set(trigger.to_role_recipients || trigger.role_recipients || []));
  const [roleRecipientsCc, setRoleRecipientsCc] = useState(new Set(trigger.cc_role_recipients || []));
  const [preview, setPreview] = useState(null);
  const [usersByRole, setUsersByRole] = useState({});
  const [waEnabled, setWaEnabled] = useState(!!(trigger.whatsapp || {}).enabled);
  const [waTemplateId, setWaTemplateId] = useState((trigger.whatsapp || {}).template_id || "");
  const [waVarMap, setWaVarMap] = useState(() => {
    const vm = (trigger.whatsapp || {}).variable_map;
    if (!vm) return "";
    if (typeof vm === "string") return vm;
    try { return JSON.stringify(vm, null, 2); } catch { return ""; }
  });
  const [waTestPhone, setWaTestPhone] = useState("");
  const [waTesting, setWaTesting] = useState(false);
  const [waLoadingVars, setWaLoadingVars] = useState(false);
  const [waTemplateVars, setWaTemplateVars] = useState(null);
  const [waTokenSamples, setWaTokenSamples] = useState([]);
  const [waRecentSends, setWaRecentSends] = useState([]);
  const [waRecentLoading, setWaRecentLoading] = useState(false);
  const [waRecentOpen, setWaRecentOpen] = useState(false);
  // Cron-based triggers get an editable schedule + threshold + "Run now" button.
  const CRON_DEFAULTS = { attendance_missing: 10, output_below_target: 20, sample_overdue: 9 };
  const isCron = Object.prototype.hasOwnProperty.call(CRON_DEFAULTS, trigger.key);
  const hasThreshold = trigger.key === "output_below_target";
  // enquiry_created is a batched hourly digest — needs its own interval knob & Run-now button,
  // but not the fixed-time HH:MM inputs (its schedule is defined by the 09:30-21:00 IST window).
  const isDigest = trigger.key === "enquiry_created";
  const [cronHour, setCronHour] = useState(trigger.cron_hour_ist ?? CRON_DEFAULTS[trigger.key] ?? 9);
  const [cronMinute, setCronMinute] = useState(trigger.cron_minute_ist ?? 0);
  const [thresholdPct, setThresholdPct] = useState(trigger.output_threshold_pct ?? 100);
  const [sharedLineSlaPct, setSharedLineSlaPct] = useState(trigger.shared_line_sla_pct ?? 80);
  const [cronIntervalMin, setCronIntervalMin] = useState(trigger.cron_interval_minutes ?? 60);
  const [escalationEmail, setEscalationEmail] = useState(trigger.escalation_user_email ?? "");
  // Per-day-of-week override on the attendance_missing cutoff time. Shape:
  // { mon: {hour: 11, minute: 0} | null, tue: {...} | null, ..., sat: {...} | null }
  // A `null` (or missing) entry means "use the default HH:MM above".
  const DOW_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat"];
  const DOW_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday" };
  const hasDowOverrides = trigger.key === "attendance_missing";
  const [dowOverrides, setDowOverrides] = useState(() => {
    const raw = trigger.cron_dow_overrides || {};
    const seed = {};
    for (const d of DOW_KEYS) {
      const v = raw[d];
      seed[d] = v && typeof v === "object" ? { hour: Number(v.hour ?? 10), minute: Number(v.minute ?? 0) } : null;
    }
    return seed;
  });
  const [runningNow, setRunningNow] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  // sample_overdue has three templates: body (MAIN) + row + overflow. The row/overflow
  // templates are injected into {{samples_rows_html}} / {{more_rows_html}} by the
  // backend renderer — never appended to the body directly.
  const isSampleOverdue = trigger.key === "sample_overdue";
  const { user: authUser } = useAuth();
  const ROW_TOKENS = ["index", "buyer_style", "bbs_style", "seq_label", "requested_by", "expected_return_date", "days_overdue"];
  const OVERFLOW_TOKENS = ["remaining_count"];
  const [rowTpl, setRowTpl] = useState(trigger.row_template_html || "");
  const [overflowTpl, setOverflowTpl] = useState(trigger.overflow_template_html || "");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const DUMMY_OVERDUE_SAMPLES = [
    { buyer: "H&M", buyer_style: "HM-24-POLO-BLU", bbs_style: "BBS-POLO-2601", seq_label: "SEQ1", requested_by: "Merch Test", expected_return_date: "2026-09-01", days_overdue: 13 },
    { buyer: "H&M", buyer_style: "HM-24-DNM-JKT", bbs_style: "BBS-DNM-118", seq_label: "SEQ2", requested_by: "Merch Test", expected_return_date: "2026-09-05", days_overdue: 9 },
    { buyer: "H&M", buyer_style: "HM-24-TEE-009", bbs_style: "BBS-TEE-2210", seq_label: "SEQ3", requested_by: "Merch Test", expected_return_date: "2026-09-10", days_overdue: 4 },
  ];
  const sendTest = async () => {
    if (!window.confirm(`Send ONE test Sample Overdue email to ${authUser?.email}?\n\nUses 3 dummy samples (no real data is touched). The live HTML comes back for inspection.`)) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/email-triggers/sample_overdue/test-send", {
        to_email: authUser?.email,
        holder_name: authUser?.name || "",
        samples: DUMMY_OVERDUE_SAMPLES,
      });
      setTestResult(data);
      if (data.ok) toast.success(`Test email sent to ${data.to}`);
      else toast.warning(`Email NOT sent: ${data.message || "check config"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    }
    setTestSending(false);
  };
  // Smart-routed triggers filter role expansion by per-event context (e.g. only the
  // Line Supervisor assigned to the line that missed attendance receives the alert).
  const SMART_ROUTED = new Set(["attendance_missing", "output_below_target"]);
  const isSmartRouted = SMART_ROUTED.has(trigger.key);
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const waMapRef = useRef(null);

  useEffect(() => {
    setSubject(trigger.subject || "");
    setBody(trigger.body_html || "");
    setEnabled(!!trigger.enabled);
    setRecipientsTo((trigger.to_recipients || trigger.recipients || []).join(", "));
    setRecipientsCc((trigger.cc_recipients || []).join(", "));
    setRoleRecipientsTo(new Set(trigger.to_role_recipients || trigger.role_recipients || []));
    setRoleRecipientsCc(new Set(trigger.cc_role_recipients || []));
    setWaEnabled(!!(trigger.whatsapp || {}).enabled);
    setWaTemplateId((trigger.whatsapp || {}).template_id || "");
    const vm = (trigger.whatsapp || {}).variable_map;
    setWaVarMap(typeof vm === "string" ? vm : (vm ? JSON.stringify(vm, null, 2) : ""));
    setPreview(null);
    // Load token samples for the WhatsApp cheatsheet chips.
    api.get("/admin/whatsapp/token-samples", { params: { trigger_key: trigger.key } })
      .then(({ data }) => setWaTokenSamples(data.tokens || []))
      .catch(() => setWaTokenSamples([]));
    // Reset per-trigger recent-sends state; it loads on demand.
    setWaRecentSends([]);
    setWaRecentOpen(false);
    setCronHour(trigger.cron_hour_ist ?? CRON_DEFAULTS[trigger.key] ?? 9);
    setCronMinute(trigger.cron_minute_ist ?? 0);
    setThresholdPct(trigger.output_threshold_pct ?? 100);
    setSharedLineSlaPct(trigger.shared_line_sla_pct ?? 80);
    setCronIntervalMin(trigger.cron_interval_minutes ?? 60);
    setEscalationEmail(trigger.escalation_user_email ?? "");
    setRowTpl(trigger.row_template_html || "");
    setOverflowTpl(trigger.overflow_template_html || "");
    setTestResult(null);
    // Reseed per-day overrides when switching triggers
    {
      const raw = trigger.cron_dow_overrides || {};
      const seed = {};
      for (const d of DOW_KEYS) {
        const v = raw[d];
        seed[d] = v && typeof v === "object" ? { hour: Number(v.hour ?? 10), minute: Number(v.minute ?? 0) } : null;
      }
      setDowOverrides(seed);
    }
  }, [trigger.key]);   // eslint-disable-line

  useEffect(() => {
    api.get("/admin/users-by-role").then(({ data }) => setUsersByRole(data)).catch(() => {});
  }, []);

  const insertToken = (tok, target = "body") => {
    const marker = `{{${tok}}}`;
    if (target === "subject") {
      const el = subjectRef.current;
      if (!el) return;
      const s = el.selectionStart ?? subject.length;
      const e = el.selectionEnd ?? subject.length;
      const next = subject.slice(0, s) + marker + subject.slice(e);
      setSubject(next);
      queueMicrotask(() => { el.focus(); el.setSelectionRange(s + marker.length, s + marker.length); });
    } else {
      const el = bodyRef.current;
      if (!el) return;
      const s = el.selectionStart ?? body.length;
      const e = el.selectionEnd ?? body.length;
      const next = body.slice(0, s) + marker + body.slice(e);
      setBody(next);
      queueMicrotask(() => { el.focus(); el.setSelectionRange(s + marker.length, s + marker.length); });
    }
  };

  const toggleRole = (role, slot) => {
    const setter = slot === "cc" ? setRoleRecipientsCc : setRoleRecipientsTo;
    setter((prev) => {
      const s = new Set(prev);
      s.has(role) ? s.delete(role) : s.add(role);
      return s;
    });
  };

  const roleCount = (role) => (usersByRole[role] || []).length;
  const resolvedRoleEmailsTo = useMemo(() => {
    const s = new Set();
    Array.from(roleRecipientsTo).forEach((r) => (usersByRole[r] || []).forEach((u) => s.add(u.email)));
    return Array.from(s);
  }, [roleRecipientsTo, usersByRole]);
  const resolvedRoleEmailsCc = useMemo(() => {
    const s = new Set();
    // Cc excludes anyone already in To (To wins).
    const toEmails = new Set([...resolvedRoleEmailsTo.map((e) => e.toLowerCase()), ...recipientsTo.split(",").map((r) => r.trim().toLowerCase()).filter(Boolean)]);
    Array.from(roleRecipientsCc).forEach((r) => (usersByRole[r] || []).forEach((u) => { if (!toEmails.has((u.email || "").toLowerCase())) s.add(u.email); }));
    return Array.from(s);
  }, [roleRecipientsCc, usersByRole, resolvedRoleEmailsTo, recipientsTo]);

  const runPreview = async () => {
    try {
      const { data } = await api.post(`/admin/email-triggers/${trigger.key}/preview`, { sample: {} });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  const save = () => {
    const toList = recipientsTo.split(",").map((r) => r.trim()).filter(Boolean);
    const ccList = recipientsCc.split(",").map((r) => r.trim()).filter(Boolean);
    let variable_map = {};
    if (waVarMap.trim()) {
      try { variable_map = JSON.parse(waVarMap); }
      catch { toast.error("WhatsApp Variable Mapping must be valid JSON"); return; }
    }
    onSave({
      subject, body_html: body, enabled,
      to_recipients: toList,
      cc_recipients: ccList,
      to_role_recipients: Array.from(roleRecipientsTo),
      cc_role_recipients: Array.from(roleRecipientsCc),
      whatsapp: { enabled: waEnabled, template_id: waTemplateId.trim(), variable_map },
      ...(isCron ? { cron_hour_ist: Number(cronHour), cron_minute_ist: Number(cronMinute) } : {}),
      ...(isSampleOverdue ? { row_template_html: rowTpl, overflow_template_html: overflowTpl } : {}),
      ...(isDigest ? { cron_interval_minutes: Number(cronIntervalMin) } : {}),
      ...(hasThreshold ? { output_threshold_pct: Number(thresholdPct), shared_line_sla_pct: Number(sharedLineSlaPct) } : {}),
      ...(isSmartRouted ? { escalation_user_email: escalationEmail.trim() } : {}),
      ...(hasDowOverrides ? {
        cron_dow_overrides: Object.fromEntries(
          DOW_KEYS
            .filter((d) => dowOverrides[d])
            .map((d) => [d, { hour: Number(dowOverrides[d].hour), minute: Number(dowOverrides[d].minute) }])
        ),
      } : {}),
    });
  };

  const runNow = async () => {
    if (!isCron && !isDigest) return;
    if (!window.confirm(`Force-run '${trigger.name}' now?\n\nThis fires the trigger immediately, bypassing the time-of-day cutoff and today's already-ran flag. Emails/WhatsApp WILL send to configured recipients.`)) return;
    setRunningNow(true);
    try {
      const { data } = await api.post(`/admin/email-triggers/${trigger.key}/run-now`);
      const r = data.result || {};
      if (r.fired_groups !== undefined) {
        // sample_overdue-style result: per-holder sends + skip breakdown
        const skips = Object.entries(r.skipped_reasons || {})
          .map(([k, v]) => `${v}× ${k.replace(/_/g, " ")}`).join(", ");
        if (r.fired_groups > 0) {
          toast.success(`Sent ${r.fired_groups} holder email(s) covering ${r.requests_covered} overdue request(s).${skips ? ` Skipped: ${skips}.` : ""}`);
        } else {
          toast.warning(`No emails sent. ${skips ? `Skipped: ${skips}.` : "Nothing overdue."} See Recent Activity for details.`, { duration: 8000 });
        }
      } else if (r.fired !== undefined) {
        toast.success(`Ran — fired ${r.fired} time(s). Check Recent Activity for details.`);
      } else if (r.skipped) {
        toast.info(`Skipped: ${r.skipped}`);
      } else {
        toast.success("Ran successfully — check Recent Activity for details.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run-now failed");
    }
    setRunningNow(false);
  };

  const backfillDigest = async () => {
    if (!isDigest) return;
    const hoursStr = window.prompt(
      "Backfill enquiries from the last how many hours?\n\n" +
      "This flags every enquiry created in that window as pending for the next digest fire. Already-digested rows will be re-flagged.\n\n" +
      "Allowed: 1 – 168 (7 days).",
      "24"
    );
    if (!hoursStr) return;
    const hours = Number(hoursStr);
    if (!Number.isFinite(hours) || hours < 1 || hours > 168) { toast.error("Enter a number between 1 and 168"); return; }
    setBackfilling(true);
    try {
      const { data } = await api.post(`/admin/email-triggers/enquiry_created/backfill`, { hours });
      const marked = data.marked ?? 0;
      if (marked === 0) {
        toast.info(`No enquiries found in the last ${hours}h.`);
      } else {
        toast.success(`Backfilled ${marked} enquiry(ies) from the last ${hours}h. They'll ship in the next digest fire — or use "Run now (force)" to ship immediately.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    }
    setBackfilling(false);
  };

  const testWhatsapp = async () => {
    if (!waTemplateId.trim()) { toast.error("Enter a Template ID first"); return; }
    if (!waTestPhone.trim()) { toast.error("Enter a phone number to send the test to"); return; }
    let variable_map;
    if (waVarMap.trim()) {
      try { variable_map = JSON.parse(waVarMap); }
      catch { toast.error("Variable Mapping must be valid JSON"); return; }
    }
    // Guard against sending the raw skeleton — Meta shows "Message Undeliverable"
    // when body values are literally "{{VAR_1}}" / "{{TOKEN_..._1}}" instead of real values.
    if (variable_map) {
      const flat = JSON.stringify(variable_map);
      if (/\{\{(VAR_|TOKEN_)/.test(flat)) {
        const proceed = window.confirm(
          "Variable Mapping still contains skeleton placeholders like {{VAR_1}}.\n\n" +
          "Meta will likely mark the message Undeliverable because these are not valid values. " +
          "Replace each placeholder with a real string OR a trigger token (e.g. {{buyer}}) before sending.\n\n" +
          "Send anyway?"
        );
        if (!proceed) return;
      }
    }
    setWaTesting(true);
    try {
      const { data } = await api.post("/admin/whatsapp/test", {
        template_id: waTemplateId.trim(),
        phone: waTestPhone.trim(),
        variable_map: variable_map || undefined,
      });
      if (data.ok) {
        // Show the exact phone we normalised + AC's message ID so the user can
        // verify the send actually left our server correctly (delivery still
        // depends on Meta / the recipient's opt-in with the WA Business number).
        const p = data.payload?.recipientPhoneNumber || "?";
        const mid = data.response?.data?.whatsappMessageId || data.response?.data?.messageId || "";
        toast.success(`WhatsApp queued → ${p}${mid ? ` (msgId ${mid.slice(0, 24)}…)` : ""}. If it doesn't arrive, check that this number is opted-in to the WA Business sender.`);
      } else {
        toast.error(`WhatsApp API said: ${JSON.stringify(data.response || data.message).slice(0, 260)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    }
    setWaTesting(false);
  };

  const loadTemplateVariables = async () => {
    const tid = waTemplateId.trim();
    if (!tid) { toast.error("Paste the Template ID first"); return; }
    setWaLoadingVars(true);
    try {
      const { data } = await api.get("/admin/whatsapp/template-variables", { params: { template_id: tid } });
      if (!data.ok) {
        toast.error(`Automate Chats said: ${JSON.stringify(data.response || data.message).slice(0, 220)}`);
        setWaTemplateVars(null);
        return;
      }
      setWaTemplateVars(data.response);
      // Auto-generate a mapping skeleton from the returned schema so users only
      // need to swap in {{tokens}} — no more parameter-count mismatch (Meta 132000).
      const resp = data.response || {};
      const inner = resp.data || resp; // Automate Chats wraps under `data`
      let skeleton = null;

      // Shape A — flat `variables` array of labels/names (Automate Chats standard).
      //   { data: { variables: ["Body Variable 1", "Body Variable 2", ...] } }
      // → simple-mode: { "variables": ["{{VAR_1}}", "{{VAR_2}}", ...] }
      if (Array.isArray(inner.variables) && inner.variables.length > 0 && inner.variables.every((v) => typeof v === "string" || typeof v === "number")) {
        skeleton = {
          variables: inner.variables.map((label, i) => `{{VAR_${i + 1}}}`),
        };
      }

      // Shape B — nested { header: {variables:[...]}, body:{variables:[...]}, buttons:[...] }
      if (!skeleton) {
        const asObj = (v) => {
          if (Array.isArray(v)) return v;
          if (v && Array.isArray(v.variables)) return v.variables;
          if (v && typeof v === "object") return Object.keys(v);
          return null;
        };
        const preciseSkel = {};
        for (const seg of ["header", "body"]) {
          const arr = asObj(inner[seg]);
          if (arr && arr.length > 0) {
            preciseSkel[seg] = {};
            arr.forEach((idx, i) => {
              const key = typeof idx === "number" ? String(idx) : String(i + 1);
              preciseSkel[seg][key] = `{{TOKEN_${seg.toUpperCase()}_${key}}}`;
            });
          }
        }
        if (Object.keys(preciseSkel).length > 0) skeleton = preciseSkel;
      }

      if (skeleton) {
        // Overwrite when textarea is blank, "{}", "[]", or holds only whitespace so
        // the auto-generated skeleton is actually useful the first time users hit
        // "Load template params" (previously the pre-filled `{}` blocked the write).
        const cur = (waVarMap || "").trim();
        const isEmptyish = !cur || cur === "{}" || cur === "[]";
        if (isEmptyish) {
          setWaVarMap(JSON.stringify(skeleton, null, 2));
          const info = Array.isArray(inner.variables) ? `${inner.variables.length} variable(s) — labels: ${inner.variables.join(" · ")}` : "mapping skeleton generated";
          toast.success(`Skeleton generated (${info}). Replace VAR_/TOKEN_ placeholders with real {{tokens}}.`);
        } else {
          toast.success(`Template expects: ${JSON.stringify(skeleton).slice(0, 220)}`);
        }
      } else {
        toast.success("Template has no variables — leave the mapping empty");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch template variables");
    }
    setWaLoadingVars(false);
  };

  const loadRecentSends = async () => {
    setWaRecentLoading(true);
    try {
      const { data } = await api.get("/admin/whatsapp/recent-sends", { params: { trigger_key: trigger.key, limit: 5 } });
      setWaRecentSends(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load recent sends");
    }
    setWaRecentLoading(false);
  };

  const insertWaToken = (tok) => {
    const marker = `{{${tok}}}`;
    const el = waMapRef.current;
    if (!el) {
      setWaVarMap((prev) => (prev || "") + marker);
      return;
    }
    const s = el.selectionStart ?? waVarMap.length;
    const e = el.selectionEnd ?? waVarMap.length;
    const next = waVarMap.slice(0, s) + marker + waVarMap.slice(e);
    setWaVarMap(next);
    queueMicrotask(() => { el.focus(); el.setSelectionRange(s + marker.length, s + marker.length); });
  };

  const ROLE_ORDER = ["admin", "admin_addl", "cad", "production", "merchandiser", "management", "line_supervisor", "ie_assistant", "floor_supervisor", "purchase", "stores", "stores_manager", "stores_jr_asst", "packing_supervisor"];
  const ROLE_CHIP_LABEL = {
    admin: "admin",
    admin_addl: "Admin Addl",
    cad: "cad",
    production: "production",
    merchandiser: "merchandiser",
    management: "management",
    line_supervisor: "line supervisor",
    ie_assistant: "ie assistant",
    floor_supervisor: "Floor Supervisor",
    purchase: "Purchase",
    stores: "Stores",
    stores_manager: "Stores Manager",
    stores_jr_asst: "Stores Jr. Asst.",
    packing_supervisor: "Packing Supervisor",
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200 rounded-md bg-white" data-testid="trigger-editor">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="font-heading font-bold text-lg truncate">{trigger.name}</div>
            <div className="text-[12.5px] text-slate-500">{trigger.description}</div>
            <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-400 mt-1">{trigger.key}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">Enabled</span>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid={`trigger-enabled-${trigger.key}`} />
          </div>
        </div>

        <div className="mt-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Subject line</div>
          <Input ref={subjectRef} value={subject} onChange={(e) => setSubject(e.target.value)} className="font-mono" data-testid="trigger-subject" />
          <TokenChips tokens={trigger.tokens || []} onInsert={(t) => insertToken(t, "subject")} label="Insert into subject" testidPrefix={`sub-token-${trigger.key}-`} />
        </div>

        <div className="mt-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1 flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Body (HTML)</div>
          <textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} className="w-full min-h-[220px] rounded-md border border-slate-200 p-3 font-mono text-[12px]" data-testid="trigger-body" />
          <TokenChips tokens={trigger.tokens || []} onInsert={(t) => insertToken(t, "body")} label="Insert into body" testidPrefix={`body-token-${trigger.key}-`} />
        </div>

        {/* sample_overdue — the ROW and OVERFLOW templates. The backend injects them into
            {{samples_rows_html}} / {{more_rows_html}} inside the MAIN body above; they are
            never appended to the body directly. */}
        {isSampleOverdue && (
          <div className="mt-5 space-y-4 rounded-md border border-amber-200 bg-amber-50/40 p-4" data-testid="sample-overdue-templates">
            <div className="text-[11.5px] text-amber-800">
              The body above is the <b>MAIN</b> template. One <b>ROW</b> template is rendered per sample into <span className="font-mono">{"{{samples_rows_html}}"}</span>; the <b>OVERFLOW</b> template renders into <span className="font-mono">{"{{more_rows_html}}"}</span> only when more than 40 samples are overdue.
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Row template (HTML) — one per sample</div>
              <textarea value={rowTpl} onChange={(e) => setRowTpl(e.target.value)} className="w-full min-h-[110px] rounded-md border border-slate-200 bg-white p-3 font-mono text-[12px]" data-testid="trigger-row-template" />
              <TokenChips tokens={ROW_TOKENS} onInsert={(t) => setRowTpl((prev) => prev + `{{${t}}}`)} label="Row tokens" testidPrefix={`row-token-${trigger.key}-`} />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Overflow template (HTML) — shown when &gt; 40 samples</div>
              <textarea value={overflowTpl} onChange={(e) => setOverflowTpl(e.target.value)} className="w-full min-h-[70px] rounded-md border border-slate-200 bg-white p-3 font-mono text-[12px]" data-testid="trigger-overflow-template" />
              <TokenChips tokens={OVERFLOW_TOKENS} onInsert={(t) => setOverflowTpl((prev) => prev + `{{${t}}}`)} label="Overflow tokens" testidPrefix={`overflow-token-${trigger.key}-`} />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button type="button" variant="outline" onClick={sendTest} disabled={testSending} className="border-brand text-brand hover:bg-rose-50" data-testid="sample-overdue-test-send-btn">
                <Send className="h-3.5 w-3.5 mr-1.5" />{testSending ? "Sending…" : "Send test to me (3 dummy samples)"}
              </Button>
              <span className="text-[11.5px] text-slate-500">Sends ONE email to your own address and shows the exact HTML below.</span>
            </div>
            {testResult && (
              <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="sample-overdue-test-result">
                <div className={`text-[12.5px] font-semibold ${testResult.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="sample-overdue-test-status">
                  {testResult.ok ? `Sent to ${testResult.to}` : `Not sent — ${testResult.message}`}
                </div>
                <div className="text-[12px] font-mono text-slate-600 mt-1" data-testid="sample-overdue-test-subject">Subject: {testResult.subject}</div>
                <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-4 text-[13px]" data-testid="sample-overdue-test-html" dangerouslySetInnerHTML={{ __html: testResult.html }} />
              </div>
            )}
          </div>
        )}

        {/* Recipients — To (action owners) + Cc (informed only). To also gets WhatsApp; Cc does not. */}
        <div className="mt-6 space-y-4">
          <div className="rounded-md border border-[#7C1D3A]/25 bg-[#7C1D3A]/5 p-4" data-testid="trigger-to-block">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[#7C1D3A] flex items-center gap-1.5">TO recipients <span className="text-[9.5px] font-mono px-1.5 py-0.5 bg-[#7C1D3A] text-white rounded">action owners</span></div>
                <div className="text-[11.5px] text-slate-600">These people appear on the <b>To:</b> line and are also messaged on WhatsApp (if configured).</div>
              </div>
              <div className="text-[11.5px] text-slate-600 font-mono" data-testid="resolved-to-count">
                Would send to <b className="text-[#7C1D3A]">{new Set([...resolvedRoleEmailsTo, ...recipientsTo.split(',').map(r=>r.trim()).filter(Boolean)]).size}</b> unique To
              </div>
            </div>
            <div className="mt-3">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1.5">Send to all users in role</div>
              <div className="flex items-center gap-2 flex-wrap">
                {ROLE_ORDER.map((r) => {
                  const on = roleRecipientsTo.has(r);
                  const n = roleCount(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRole(r, "to")}
                      data-testid={`role-chip-to-${r}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[12px] font-semibold capitalize transition-colors ${on ? "bg-[#7C1D3A] text-white border-[#7C1D3A]" : "bg-white text-slate-700 border-slate-200 hover:border-[#7C1D3A]/50 hover:text-[#7C1D3A]"}`}
                    >
                      {ROLE_CHIP_LABEL[r] || r} <span className={`text-[10px] font-mono px-1 rounded ${on ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{n}</span>
                    </button>
                  );
                })}
              </div>
              {roleRecipientsTo.size > 0 && (
                <div className="mt-2 text-[11px] font-mono text-slate-500" data-testid="resolved-to-emails-list">
                  Resolves to: {resolvedRoleEmailsTo.length ? resolvedRoleEmailsTo.join(", ") : <span className="italic text-slate-400">no active users in these roles yet</span>}
                </div>
              )}
            </div>
            <div className="mt-4">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1.5">Additional emails (comma-separated)</div>
              <Input value={recipientsTo} onChange={(e) => setRecipientsTo(e.target.value)} className="font-mono bg-white" placeholder="ceo@bbsglobal.in, ops@bbsglobal.in" data-testid="trigger-to-recipients" />
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid="trigger-cc-block">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">CC recipients <span className="text-[9.5px] font-mono px-1.5 py-0.5 bg-slate-500 text-white rounded">informed only</span></div>
                <div className="text-[11.5px] text-slate-500">These people appear on the <b>Cc:</b> line. Email only — no WhatsApp. Anyone already on To is auto-removed from Cc.</div>
              </div>
              <div className="text-[11.5px] text-slate-500 font-mono" data-testid="resolved-cc-count">
                Would cc <b className="text-slate-700">{new Set([...resolvedRoleEmailsCc, ...recipientsCc.split(',').map(r=>r.trim()).filter(Boolean)]).size}</b> unique Cc
              </div>
            </div>
            <div className="mt-3">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1.5">Send to all users in role</div>
              <div className="flex items-center gap-2 flex-wrap">
                {ROLE_ORDER.map((r) => {
                  const on = roleRecipientsCc.has(r);
                  const n = roleCount(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRole(r, "cc")}
                      data-testid={`role-chip-cc-${r}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[12px] font-semibold capitalize transition-colors ${on ? "bg-slate-600 text-white border-slate-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-500 hover:text-slate-800"}`}
                    >
                      {ROLE_CHIP_LABEL[r] || r} <span className={`text-[10px] font-mono px-1 rounded ${on ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{n}</span>
                    </button>
                  );
                })}
              </div>
              {roleRecipientsCc.size > 0 && (
                <div className="mt-2 text-[11px] font-mono text-slate-500" data-testid="resolved-cc-emails-list">
                  Resolves to: {resolvedRoleEmailsCc.length ? resolvedRoleEmailsCc.join(", ") : <span className="italic text-slate-400">no additional users (all overlap with To)</span>}
                </div>
              )}
            </div>
            <div className="mt-4">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1.5">Additional emails (comma-separated)</div>
              <Input value={recipientsCc} onChange={(e) => setRecipientsCc(e.target.value)} className="font-mono bg-white" placeholder="audit@bbsglobal.in" data-testid="trigger-cc-recipients" />
            </div>
          </div>
        </div>

        {/* Cron schedule + threshold (only for daily-cron triggers). "Run now" bypasses time-of-day + day-flag. */}
        {isCron && (
          <div className="mt-6 rounded-md border border-indigo-200 bg-indigo-50/40 p-4" data-testid="trigger-cron-block">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-800 flex items-center gap-1.5">Schedule <span className="text-[9.5px] font-mono px-1.5 py-0.5 bg-indigo-600 text-white rounded">DAILY CRON</span></div>
                <div className="text-[11.5px] text-slate-600">
                  Fires once per day (IST). Editable time + threshold; changes apply on next 15-min loop tick.{hasDowOverrides ? " Per-day overrides below let you set a different cutoff on specific weekdays." : ""}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={runNow}
                disabled={runningNow}
                data-testid="trigger-run-now-btn"
                className="border-indigo-300 text-indigo-800 hover:bg-indigo-100"
              >
                <Zap className="h-4 w-4 mr-1.5" /> {runningNow ? "Running…" : "Run now (force)"}
              </Button>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1 block">Fire at (IST · HH:MM)</label>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={cronHour}
                    onChange={(e) => setCronHour(e.target.value)}
                    className="font-mono bg-white h-9 w-20 text-center"
                    data-testid="trigger-cron-hour"
                  />
                  <span className="text-slate-500 font-mono text-[16px]">:</span>
                  <Input
                    type="number"
                    min={0}
                    max={59}
                    step={5}
                    value={cronMinute}
                    onChange={(e) => setCronMinute(e.target.value)}
                    className="font-mono bg-white h-9 w-20 text-center"
                    data-testid="trigger-cron-minute"
                  />
                  <span className="text-[11px] text-slate-500 ml-1">IST</span>
                </div>
                <div className="text-[10.5px] text-slate-500 mt-1">
                  Default {String(CRON_DEFAULTS[trigger.key]).padStart(2,"0")}:00 IST. Effective precision is ~5 min because the scheduler loop wakes every 5 minutes (e.g. setting 10:15 fires between 10:15 and 10:20).
                </div>
              </div>
              {hasThreshold && (
                <div>
                  <label className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1 block">Fire when actual &lt; % of daily_target</label>
                  <div className="relative">
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={thresholdPct}
                      onChange={(e) => setThresholdPct(e.target.value)}
                      className="font-mono bg-white h-9 pr-8"
                      data-testid="trigger-threshold-pct"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">%</span>
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-1">
                    Default 100 (fire at any shortfall). Set to <span className="font-mono">80</span> to only chirp when a line falls below 80% of its daily target.
                  </div>
                </div>
              )}
              {hasThreshold && (
                <div>
                  <label className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1 block">Shared-line SLA cushion</label>
                  <div className="relative">
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={sharedLineSlaPct}
                      onChange={(e) => setSharedLineSlaPct(e.target.value)}
                      className="font-mono bg-white h-9 pr-8"
                      data-testid="trigger-shared-line-sla-pct"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">%</span>
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-1">
                    When 2+ SOs share the same line, target = <span className="font-mono">min(sum of per-SO targets, line's daily capacity)</span>. Alert fires only when the line's combined output drops below <b>this %</b> of that. Default <span className="font-mono">80</span>. Set line capacity in Masters → Lines.
                  </div>
                </div>
              )}
            </div>
            {hasDowOverrides && (
              <div className="mt-4 pt-3 border-t border-indigo-200" data-testid="dow-overrides-block">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-800 flex items-center gap-1.5 mb-1">
                  Per-day override <span className="text-[9.5px] font-mono px-1.5 py-0.5 bg-indigo-100 text-indigo-700 border border-indigo-300 rounded">OPTIONAL</span>
                </div>
                <div className="text-[11.5px] text-slate-600 mb-2 leading-relaxed">
                  Set a different cutoff on specific weekdays. Days with the override <b>OFF</b> use the default HH:MM above. Sunday is always skipped (off-day).
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {DOW_KEYS.map((d) => {
                    const active = !!dowOverrides[d];
                    const hh = dowOverrides[d]?.hour ?? Number(cronHour ?? 10);
                    const mm = dowOverrides[d]?.minute ?? Number(cronMinute ?? 0);
                    return (
                      <div key={d} className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5" data-testid={`dow-row-${d}`}>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none text-[12px] font-medium text-slate-700 w-[88px]">
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={(e) => setDowOverrides((prev) => ({
                              ...prev,
                              [d]: e.target.checked ? { hour: Number(cronHour ?? 10), minute: Number(cronMinute ?? 0) } : null,
                            }))}
                            data-testid={`dow-toggle-${d}`}
                            className="h-3.5 w-3.5 accent-indigo-600"
                          />
                          {DOW_LABELS[d]}
                        </label>
                        <div className="flex items-center gap-1 ml-auto">
                          <Input
                            type="number"
                            min={0} max={23}
                            value={hh}
                            disabled={!active}
                            onChange={(e) => setDowOverrides((prev) => ({ ...prev, [d]: { hour: Number(e.target.value), minute: prev[d]?.minute ?? 0 } }))}
                            className={`font-mono h-8 w-14 text-center ${active ? "bg-white" : "bg-slate-100 text-slate-400"}`}
                            data-testid={`dow-hour-${d}`}
                          />
                          <span className="text-slate-400 font-mono">:</span>
                          <Input
                            type="number"
                            min={0} max={59} step={5}
                            value={mm}
                            disabled={!active}
                            onChange={(e) => setDowOverrides((prev) => ({ ...prev, [d]: { hour: prev[d]?.hour ?? Number(cronHour ?? 10), minute: Number(e.target.value) } }))}
                            className={`font-mono h-8 w-14 text-center ${active ? "bg-white" : "bg-slate-100 text-slate-400"}`}
                            data-testid={`dow-minute-${d}`}
                          />
                          <span className="text-[10.5px] text-slate-500 ml-1 w-8">{active ? "IST" : "def."}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {isSmartRouted && (
              <div className="mt-4 pt-3 border-t border-indigo-200">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-800 flex items-center gap-1.5 mb-1">Smart routing <span className="text-[9.5px] font-mono px-1.5 py-0.5 bg-indigo-100 text-indigo-700 border border-indigo-300 rounded">ON</span></div>
                <div className="text-[11.5px] text-slate-600 mb-2 leading-relaxed">
                  {trigger.key === "attendance_missing"
                    ? <>When you tick <b>Line Supervisor</b> in TO or CC, this trigger sends only to the specific supervisor assigned to the line that missed attendance (via Masters → Production Lines → Supervisor). Other role ticks (Production, Merch, etc.) still fan out normally.</>
                    : <>When you tick <b>Line Supervisor</b> in TO or CC, this trigger sends only to the supervisor(s) of the SO's <b>assigned line(s)</b> (via order.assigned_lines → line.supervisor). Other role ticks fan out normally.</>}
                </div>
                <label className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1 block">Escalation fallback email (used when no supervisor matches)</label>
                <Input
                  type="email"
                  placeholder="prodhead@bbsglobal.in"
                  value={escalationEmail}
                  onChange={(e) => setEscalationEmail(e.target.value)}
                  className="font-mono bg-white h-9"
                  data-testid="trigger-escalation-email"
                />
                <div className="text-[10.5px] text-slate-500 mt-1">
                  If the assigned supervisor isn't a Line Supervisor role user (or a line has no supervisor set), the alert is redirected here so nothing is silently dropped. Leave blank to skip the role for un-matched fires (static emails + other roles still fire).
                </div>
              </div>
            )}
          </div>
        )}

        {/* Digest interval control for the batched enquiry_created trigger. Fixed 09:30–21:00 IST window. */}
        {isDigest && (
          <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50/40 p-4" data-testid="trigger-digest-block">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">Batched digest <span className="text-[9.5px] font-mono px-1.5 py-0.5 bg-emerald-600 text-white rounded">CLIENT-WISE</span></div>
                <div className="text-[11.5px] text-slate-600 mt-0.5">
                  Fires every <b>{cronIntervalMin} min</b> between <b>09:30 – 21:00 IST</b>, one message per buyer summarising that buyer's new enquiries. Enquiries added after 21:00 IST are held for the next day's 09:30 batch.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={backfillDigest}
                  disabled={backfilling}
                  data-testid="trigger-backfill-btn"
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" /> {backfilling ? "Backfilling…" : "Backfill last 24h"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={runNow}
                  disabled={runningNow}
                  data-testid="trigger-run-now-btn"
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                >
                  <Zap className="h-4 w-4 mr-1.5" /> {runningNow ? "Running…" : "Run now (force)"}
                </Button>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1 block">Digest interval (minutes)</label>
                <div className="relative">
                  <Input
                    type="number"
                    min={15}
                    max={240}
                    step={5}
                    value={cronIntervalMin}
                    onChange={(e) => setCronIntervalMin(e.target.value)}
                    className="font-mono bg-white h-9 pr-14"
                    data-testid="trigger-cron-interval"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[11px]">min</span>
                </div>
                <div className="text-[10.5px] text-slate-500 mt-1">
                  Default 60 (hourly). Allowed 15 – 240. Effective precision is ~5 min because the scheduler loop wakes every 5 minutes.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={runPreview} data-testid="trigger-preview-btn"><Eye className="h-4 w-4 mr-2" /> Refresh preview</Button>
          <Button variant="outline" onClick={onReset} disabled={busy} data-testid="trigger-reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset to default</Button>
          <div className="flex-1" />
          <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="trigger-save-btn"><Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save"}</Button>
        </div>
      </Card>

      <Card className="p-5 border-slate-200 rounded-md bg-white" data-testid="trigger-preview">
        <div className="flex items-center gap-2 mb-3"><Eye className="h-4 w-4 text-brand" /> <div className="font-heading font-bold text-lg">Live preview (with sample data)</div></div>
        {!preview ? (
          <div className="text-[12.5px] text-slate-500">Click <b>Refresh preview</b> to render the trigger with realistic sample values and see the resolved recipient list.</div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="text-[10.5px] font-mono uppercase tracking-widest text-[#7C1D3A]">Effective To ({preview.effective_to?.length || 0})</div>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="preview-effective-to">
                {(preview.effective_to || []).map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#7C1D3A]/10 text-[11px] font-mono text-[#7C1D3A] border border-[#7C1D3A]/25">{e}</span>
                ))}
                {(preview.effective_to || []).length === 0 && <span className="text-[11px] italic text-slate-400">No To recipients — pick a role or add an email.</span>}
              </div>
            </div>
            <div>
              <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">Effective Cc ({preview.effective_cc?.length || 0})</div>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="preview-effective-cc">
                {(preview.effective_cc || []).map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-[11px] font-mono text-slate-700 border border-slate-200">{e}</span>
                ))}
                {(preview.effective_cc || []).length === 0 && <span className="text-[11px] italic text-slate-400">No Cc recipients.</span>}
              </div>
            </div>
            <div>
              <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-400">Subject</div>
              <div className="mt-0.5 text-[13.5px] font-semibold">{preview.subject}</div>
            </div>
            <div>
              <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-400">Body</div>
              <div className="mt-0.5 rounded-md border border-slate-200 bg-slate-50 p-4 text-[13px]" dangerouslySetInnerHTML={{ __html: preview.body_html }} />
            </div>
          </div>
        )}
      </Card>

      {/* WhatsApp — Automate Chats */}
      <Card className="p-5 border-slate-200 rounded-md bg-white" data-testid="trigger-whatsapp-panel">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="font-heading font-semibold text-[15px] flex items-center gap-2">
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500 text-white text-[10px] font-bold">W</span>
              WhatsApp (Automate Chats)
            </div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              When this trigger fires, ALSO send a WhatsApp using an approved template. Phone numbers come from each recipient's user profile.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-[12.5px] cursor-pointer">
            <input type="checkbox" checked={waEnabled} onChange={(e) => setWaEnabled(e.target.checked)} className="h-4 w-4 accent-emerald-600" data-testid="wa-enable" />
            <span className="font-semibold">{waEnabled ? "Enabled" : "Disabled"}</span>
          </label>
        </div>
        <div className={waEnabled ? "space-y-3" : "space-y-3 opacity-60 pointer-events-none"}>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Template ID (UUID)</label>
            <div className="flex gap-2">
              <Input value={waTemplateId} onChange={(e) => setWaTemplateId(e.target.value)} placeholder="e.g. 593c53d1-83fe-4846-a971-3fac76b75195" className="mt-1 font-mono text-[12.5px] flex-1" data-testid="wa-template-id" />
              <Button type="button" variant="outline" onClick={loadTemplateVariables} disabled={waLoadingVars} className="mt-1 whitespace-nowrap" data-testid="wa-load-variables">
                {waLoadingVars ? "Loading…" : "Load template params"}
              </Button>
            </div>
            {waTemplateVars && (
              <div className="mt-2 text-[11px] font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-2 max-h-32 overflow-auto" data-testid="wa-template-vars-preview">
                {JSON.stringify(waTemplateVars, null, 2)}
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              Variable Mapping (JSON — optional)
            </label>
            <div className="text-[11px] text-slate-500 mb-1">
              Use <code className="font-mono">{"{{token}}"}</code> placeholders — same tokens available above. Example:
              <br />
              <code className="font-mono text-[10.5px]">{`{ "body": { "1": "{{buyer}}", "2": "{{our_style}}" } }`}</code>
            </div>
            <WaTokenCheatsheet tokens={waTokenSamples} onInsert={insertWaToken} triggerKey={trigger.key} />
            {(() => {
              // Actor-token safety check — many templates have a role like {{supervisor}} or
              // {{submitted_by}}. If the admin accidentally maps that slot to {{recipient_name}}
              // the WhatsApp will show the phone-recipient's own name instead. Warn early.
              const ACTOR_TOKENS = ["supervisor", "submitted_by", "approved_by", "rejected_by", "editor", "cad_submitted_by", "production_submitted_by", "sent_for_approval_by"];
              const availableActors = (waTokenSamples || [])
                .map((t) => t.token)
                .filter((t) => ACTOR_TOKENS.includes(t));
              const usesRecipient = /\{\{\s*recipient_name\s*\}\}/.test(waVarMap || "");
              if (usesRecipient && availableActors.length > 0) {
                return (
                  <div className="mb-2 rounded border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11.5px] text-amber-900 leading-snug" data-testid="wa-varmap-actor-warning">
                    <b>⚠ Heads-up:</b> Your mapping uses <code className="font-mono">{"{{recipient_name}}"}</code> — that resolves to whoever's phone the message goes to (often not the actor). This trigger also exposes <b>{availableActors.map((t) => `{{${t}}}`).join(", ")}</b>. Double-check the slot that names the actor points at the right token, otherwise the message will read "Supervisor: <i>&lt;recipient's own name&gt;</i>".
                  </div>
                );
              }
              return null;
            })()}
            <textarea
              ref={waMapRef}
              value={waVarMap}
              onChange={(e) => setWaVarMap(e.target.value)}
              placeholder='{ "body": { "1": "{{buyer}}", "2": "{{link}}" } }'
              rows={5}
              className="w-full font-mono text-[12px] rounded-md border border-slate-200 p-2"
              data-testid="wa-variable-map"
            />
          </div>
          <div className="flex flex-wrap gap-2 items-end pt-1 border-t border-slate-100">
            <div className="flex-1 min-w-[220px]">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Send a test to phone</label>
              <Input value={waTestPhone} onChange={(e) => setWaTestPhone(e.target.value)} placeholder="9876543210 (country code auto-added)" className="mt-1 font-mono" data-testid="wa-test-phone" />
            </div>
            <Button type="button" variant="outline" onClick={testWhatsapp} disabled={waTesting} data-testid="wa-test-send">
              {waTesting ? "Sending…" : "Send test"}
            </Button>
          </div>
        </div>
        {/* Last 5 sends debug panel — always visible (independent of the WA-enable switch so
            admins can inspect prior sends even after they disable a trigger temporarily). */}
        <WaRecentSendsPanel
          triggerKey={trigger.key}
          rows={waRecentSends}
          loading={waRecentLoading}
          open={waRecentOpen}
          onToggle={() => { const next = !waRecentOpen; setWaRecentOpen(next); if (next && waRecentSends.length === 0) loadRecentSends(); }}
          onRefresh={loadRecentSends}
        />
      </Card>
    </div>
  );
}

function TokenChips({ tokens, onInsert, label, testidPrefix }) {
  if (!tokens?.length) return null;
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      <span className="text-[10.5px] uppercase tracking-widest text-slate-400 mr-1">{label}:</span>
      {tokens.map((t) => (
        <button key={t} onClick={() => onInsert(t)} data-testid={`${testidPrefix}${t}`} className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 hover:bg-[#7C1D3A]/5 hover:border-[#7C1D3A] text-[11px] font-mono text-slate-700">
          {`{{${t}}}`}
        </button>
      ))}
    </div>
  );
}

// Cheatsheet — WhatsApp token chips with the sample value shown alongside.
// Clicking a chip inserts `{{token}}` into the mapping textarea at the cursor.
function WaTokenCheatsheet({ tokens, onInsert, triggerKey }) {
  if (!tokens || tokens.length === 0) return null;
  return (
    <div className="mb-2 rounded-md border border-emerald-100 bg-emerald-50/50 p-2" data-testid="wa-token-cheatsheet">
      <div className="text-[10.5px] uppercase tracking-widest text-emerald-800 font-semibold mb-1.5">
        Available tokens · click to insert
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {tokens.map((t) => (
          <button
            key={t.token}
            type="button"
            onClick={() => onInsert(t.token)}
            data-testid={`wa-token-chip-${triggerKey}-${t.token}`}
            title={`Sample: ${t.sample || "—"}`}
            className="group inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-200 bg-white hover:bg-emerald-100 hover:border-emerald-400 text-[11px] font-mono text-emerald-900 transition-colors"
          >
            <span className="font-semibold">{`{{${t.token}}}`}</span>
            {t.sample && (
              <>
                <span className="text-emerald-400">→</span>
                <span className="text-emerald-700 group-hover:text-emerald-900 max-w-[140px] truncate">{t.sample}</span>
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// Collapsible "Last 5 sends" debug panel for a single trigger.
function WaRecentSendsPanel({ triggerKey, rows, loading, open, onToggle, onRefresh }) {
  return (
    <div className="mt-4 border-t border-slate-100 pt-3" data-testid="wa-recent-sends-panel">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onToggle}
          data-testid="wa-recent-sends-toggle"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 hover:text-[#7C1D3A]"
        >
          <span className={`inline-block transition-transform ${open ? "rotate-90" : ""}`}>▸</span>
          Debug · Last 5 sends {rows.length > 0 && <span className="text-[10.5px] font-mono text-slate-400">({rows.length})</span>}
        </button>
        {open && (
          <button
            type="button"
            onClick={onRefresh}
            data-testid="wa-recent-sends-refresh"
            className="text-[11px] text-slate-500 hover:text-[#7C1D3A] underline"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          {loading && rows.length === 0 ? (
            <div className="text-[12px] text-slate-500 py-3 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-[12px] text-slate-500 py-3 text-center italic">No WhatsApp sends recorded for this trigger yet. Fire a test send or wait for a production trigger.</div>
          ) : (
            rows.map((r) => <WaSendRow key={r.id} row={r} />)
          )}
        </div>
      )}
    </div>
  );
}

function WaSendRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const attempts = Array.isArray(row.attempts) && row.attempts.length > 0
    ? row.attempts
    : (row.payload
        ? [{ payload: row.payload, response: row.ac_response, ok: row.status === "sent", whatsapp_message_id: row.whatsapp_message_id, ac_message_id: row.ac_message_id, phone: (row.delivered?.[0]?.phone || row.failed?.[0]?.phone || "") }]
        : []);
  const statusTone = row.status === "sent" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : row.status === "partial" ? "text-amber-700 bg-amber-50 border-amber-200"
    : row.status === "failed" ? "text-rose-700 bg-rose-50 border-rose-200"
    : "text-slate-600 bg-slate-50 border-slate-200";
  const when = row.at ? new Date(row.at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
  const wamid = attempts.find((a) => a.whatsapp_message_id)?.whatsapp_message_id || row.whatsapp_message_id || "";
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); } catch { toast.error("Copy failed"); }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-white text-[11.5px]" data-testid={`wa-send-row-${row.id}`}>
      <button type="button" onClick={() => setExpanded((v) => !v)} className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-slate-50">
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${statusTone}`}>{(row.status || "").toUpperCase()}</span>
        <span className="font-mono text-slate-500 whitespace-nowrap">{when}</span>
        {row.trigger_key === "__manual_test__" ? (
          <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">TEST</span>
        ) : null}
        <span className="flex-1 truncate text-slate-700">
          {attempts.length > 0 ? attempts.map((a) => a.phone).filter(Boolean).join(", ") : ((row.recipients || []).join(", ") || "—")}
        </span>
        {wamid ? <span className="font-mono text-[10.5px] text-slate-400 truncate max-w-[180px]">wamid: {wamid.slice(0, 20)}…</span> : null}
        <span className={`inline-block transition-transform text-slate-400 ${expanded ? "rotate-90" : ""}`}>▸</span>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 p-3 space-y-2 bg-slate-50/40">
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div><span className="text-slate-400 uppercase text-[9.5px] tracking-widest">Template</span><div className="font-mono text-slate-700 truncate">{row.template_id || "—"}</div></div>
            <div><span className="text-slate-400 uppercase text-[9.5px] tracking-widest">Trigger</span><div className="font-mono text-slate-700 truncate">{row.trigger_key || "—"}</div></div>
          </div>
          {row.reason && (
            <div className="text-[11px] rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1">Skipped reason: <span className="font-mono">{row.reason}</span></div>
          )}
          {(row.no_phone && row.no_phone.length > 0) ? (
            <div className="text-[11px] rounded-md bg-slate-100 border border-slate-200 px-2 py-1">
              <span className="text-slate-500">No phone on file for:</span> <span className="font-mono">{row.no_phone.join(", ")}</span>
            </div>
          ) : null}
          {attempts.length === 0 ? (
            <div className="text-[11px] italic text-slate-500">No outbound payload recorded (send was skipped before dispatch).</div>
          ) : (
            attempts.map((a, idx) => (
              <div key={idx} className="rounded-md border border-slate-200 bg-white p-2 space-y-1.5">
                <div className="flex items-center gap-2 text-[11px]">
                  <span className={`text-[9.5px] font-semibold px-1.5 py-0.5 rounded border ${a.ok ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-rose-700 bg-rose-50 border-rose-200"}`}>{a.ok ? "OK" : "FAIL"}</span>
                  <span className="font-mono text-slate-700">{a.phone || "—"}</span>
                  {a.email ? <span className="text-slate-400">· {a.email}</span> : null}
                  {a.status_code ? <span className="ml-auto text-slate-400 font-mono">HTTP {a.status_code}</span> : null}
                </div>
                {a.whatsapp_message_id && (
                  <div className="flex items-center gap-2 text-[10.5px]">
                    <span className="text-slate-400 uppercase text-[9.5px] tracking-widest">wamid</span>
                    <code className="font-mono text-slate-700 truncate flex-1">{a.whatsapp_message_id}</code>
                    <button type="button" onClick={() => copy(a.whatsapp_message_id, "wamid")} className="text-[10px] text-slate-500 hover:text-[#7C1D3A] underline">copy</button>
                  </div>
                )}
                {a.payload && (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 uppercase text-[9.5px] tracking-widest">Payload</span>
                      <button type="button" onClick={() => copy(JSON.stringify(a.payload, null, 2), "Payload JSON")} className="text-[10px] text-slate-500 hover:text-[#7C1D3A] underline">copy</button>
                    </div>
                    <pre className="mt-0.5 max-h-40 overflow-auto rounded bg-slate-900 text-slate-100 p-2 text-[10.5px] font-mono">{JSON.stringify(a.payload, null, 2)}</pre>
                  </div>
                )}
                {a.response && (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 uppercase text-[9.5px] tracking-widest">AC response</span>
                      <button type="button" onClick={() => copy(JSON.stringify(a.response, null, 2), "AC response")} className="text-[10px] text-slate-500 hover:text-[#7C1D3A] underline">copy</button>
                    </div>
                    <pre className="mt-0.5 max-h-40 overflow-auto rounded bg-slate-100 text-slate-800 p-2 text-[10.5px] font-mono">{JSON.stringify(a.response, null, 2)}</pre>
                  </div>
                )}
                {a.err && !a.ok && (
                  <div className="text-[10.5px] text-rose-800 bg-rose-50 border border-rose-200 rounded p-1.5 font-mono">
                    {typeof a.err === "string" ? a.err : JSON.stringify(a.err).slice(0, 400)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}


// ---------------- Recent activity (email log) ----------------
function LogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("all");
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-log", { params: { limit: 200 } });
      setRows(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load log"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => status === "all" ? rows : rows.filter((r) => r.status === status), [rows, status]);

  const iconFor = (s) => s === "sent" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : s === "partial" ? <CheckCircle2 className="h-4 w-4 text-amber-600" /> : s === "failed" ? <XCircle className="h-4 w-4 text-rose-600" /> : <MinusCircle className="h-4 w-4 text-slate-400" />;
  const tone = (s) => s === "sent" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : s === "partial" ? "bg-amber-50 text-amber-800 border-amber-200" : s === "failed" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-slate-50 text-slate-600 border-slate-200";
  const humanReason = (r) => {
    const m = { email_disabled: "Email delivery is turned OFF in SMTP settings", trigger_disabled: "This trigger is not enabled", trigger_not_found: "Trigger not found", no_recipients: "No To recipients (and no Cc to promote either)" };
    return m[r] || r || "";
  };

  return (
    <Card className="p-4 space-y-3" data-testid="email-log-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-[13px] text-slate-600">Latest 200 trigger firings. Skipped rows tell you WHY an email didn't send — check the reason column.</div>
        <div className="ml-auto flex gap-2 items-center">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-[12.5px] border border-slate-200 rounded px-2 py-1.5" data-testid="email-log-status-filter">
            <option value="all">All</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}><RotateCcw className="h-4 w-4 mr-1" /> Refresh</Button>
        </div>
      </div>
      {loading ? (
        <div className="text-[13px] text-slate-500 py-6 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-[13px] text-slate-500 py-6 text-center">
          {rows.length === 0 ? "No emails have been fired yet. Once someone submits/approves an enquiry or bulletin, entries will appear here." : "No entries match this filter."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="opsiq-table">
            <thead>
              <tr><th style={{ width: 30 }}></th><th style={{ width: 160 }}>When</th><th style={{ width: 220 }}>Trigger</th><th>To / Cc</th><th style={{ width: 120 }}>Status</th><th>Reason / delivery</th></tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`log-row-${r.id}`}>
                  <td>{iconFor(r.status)}</td>
                  <td className="text-[12px] text-slate-600 whitespace-nowrap">{new Date(r.at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="font-mono text-[11.5px]">{r.trigger_key}</td>
                  <td className="text-[12px] text-slate-700">
                    {(r.to_recipients || r.cc_recipients) ? (
                      <div className="space-y-0.5">
                        {(r.to_recipients || []).length > 0 && <div><span className="text-[9.5px] font-mono uppercase text-[#7C1D3A] mr-1">To</span>{(r.to_recipients || []).join(", ")}</div>}
                        {(r.cc_recipients || []).length > 0 && <div><span className="text-[9.5px] font-mono uppercase text-slate-500 mr-1">Cc</span>{(r.cc_recipients || []).join(", ")}</div>}
                        {(r.to_recipients || []).length === 0 && (r.cc_recipients || []).length === 0 && <span className="text-slate-400 italic">—</span>}
                      </div>
                    ) : ((r.recipients || []).join(", ") || <span className="text-slate-400 italic">—</span>)}
                  </td>
                  <td><span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded border ${tone(r.status)}`}>{r.status?.toUpperCase()}</span></td>
                  <td className="text-[12px] text-slate-600">
                    {r.status === "skipped" && humanReason(r.reason)}
                    {r.status === "sent" && <span>Delivered to {(r.delivered || []).length} recipient(s)</span>}
                    {r.status === "partial" && <span>{(r.delivered || []).length} ok · {(r.failed || []).length} failed{r.failed && r.failed[0] ? ` (${r.failed[0].err})` : ""}</span>}
                    {r.status === "failed" && ((r.failed || [])[0]?.err || "Delivery failed")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------- Global WhatsApp Activity (last 100 WA sends across all triggers) ----------------
function WhatsAppActivityPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("all");
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/whatsapp/recent-sends", { params: { limit: 50 } });
      setRows(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load WhatsApp log"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => status === "all" ? rows : rows.filter((r) => r.status === status), [rows, status]);

  return (
    <Card className="p-4 space-y-3" data-testid="whatsapp-activity-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-[13px] text-slate-600">Latest 50 WhatsApp sends (production triggers + manual tests). Expand any row to see the exact outbound payload, AC response, and wamid.</div>
        <div className="ml-auto flex gap-2 items-center">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-[12.5px] border border-slate-200 rounded px-2 py-1.5" data-testid="wa-log-status-filter">
            <option value="all">All</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="wa-log-refresh"><RotateCcw className="h-4 w-4 mr-1" /> Refresh</Button>
        </div>
      </div>
      {loading && rows.length === 0 ? (
        <div className="text-[13px] text-slate-500 py-6 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-[13px] text-slate-500 py-6 text-center">
          {rows.length === 0 ? "No WhatsApp sends recorded yet. Send a test from any trigger's WhatsApp panel to see it here." : "No entries match this filter."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => <WaSendRow key={r.id} row={r} />)}
        </div>
      )}
    </Card>
  );
}
